/**
 * DistrictFilter plugin - conditional multi-select filter for districts (powiaty).
 *
 * This is a conditional filter (DD-6): Component returns null when loaded + no data.
 * Shows skeleton during isLoading to prevent layout shift.
 *
 * URL param: district (singular, multiple values via getAll)
 * API param: districts (plural)
 * SavedView field: districts
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4, DD-6)
 */

import React, { useMemo } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";
import MultiSelectDropdown from "../../../../common/MultiSelectDropdown";
import { DistrictCount } from "../../../../../types/grants";

/**
 * District filter React component.
 * Conditional: returns null when loaded + no data (DD-6).
 */
const DistrictFilterComponent = React.memo(function DistrictFilterComponent(
  props: FilterComponentProps<string[]>
) {
  const { value, onChange, facets, isLoading } = props;

  const options = useMemo(
    () =>
      ((facets?.districtCounts as DistrictCount[]) ?? []).map((dc) => ({
        value: dc.slug,
        label: dc.name,
        count: dc.count,
      })),
    [facets?.districtCounts]
  );

  // DD-6: Show skeleton during loading to prevent layout shift
  if (isLoading) {
    return React.createElement("div", {
      className: "w-64 h-10 bg-neutral-200 rounded-md animate-pulse",
    });
  }

  // DD-6: Conditional rendering - hide when no districts available and none selected
  if (options.length === 0 && value.length === 0) {
    return null;
  }

  return React.createElement(
    "div",
    { className: "w-64 relative" },
    React.createElement(MultiSelectDropdown, {
      options,
      selectedValues: value,
      onChange,
      placeholder: "Wszystkie powiaty",
      ariaLabel: "Filtruj według powiatu",
    })
  );
});

export const districtFilter: FilterPlugin<string[]> = {
  key: "district",
  label: "Powiat",
  placement: "main",
  order: 5,
  defaultValue: [],
  Component: DistrictFilterComponent,

  isActive(value: string[]): boolean {
    return value.length > 0;
  },

  toUrlParams(value: string[], params: URLSearchParams): void {
    value.forEach((d) => params.append("district", d));
  },

  fromUrlParams(params: URLSearchParams): string[] {
    return params.getAll("district");
  },

  toSavedView(value: string[], filters: Partial<SavedViewFilters>): void {
    filters.districts = value;
  },

  fromSavedView(filters: SavedViewFilters): string[] {
    return filters.districts ?? [];
  },

  toApiParams(value: string[], params: URLSearchParams): void {
    value.forEach((d) => params.append("districts", d));
  },
};
